import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Mic, MicOff, X, Loader2, Sparkles, Volume2, Play, ExternalLink } from "lucide-react";
import api, { API } from "@/lib/api";
import { extractUrls, renderWithLinks } from "@/lib/format.jsx";

const VOICE_SESSION = "thn_voice_session";

export default function VoiceAgent() {
  const [open, setOpen] = useState(false);
  const [supported, setSupported] = useState(true);
  const [permError, setPermError] = useState("");
  const [listening, setListening] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [reply, setReply] = useState("");
  const [products, setProducts] = useState([]);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(VOICE_SESSION) || null);

  const recRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) setSupported(false);
    api.get("/products").then((r) => setProducts(r.data.products)).catch(() => {});
  }, []);

  const stopAll = () => {
    try { recRef.current && recRef.current.stop(); } catch {}
    try { audioRef.current && audioRef.current.pause(); } catch {}
    setListening(false);
    setSpeaking(false);
  };

  useEffect(() => {
    if (!open) stopAll();
  }, [open]);

  const startListening = () => {
    setPermError("");
    setTranscript("");
    setReply("");
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      setSupported(false);
      return;
    }
    const rec = new SR();
    rec.lang = "en-US";
    rec.interimResults = true;
    rec.continuous = false;
    rec.maxAlternatives = 1;

    let finalText = "";
    rec.onresult = (e) => {
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) finalText += t;
        else interim += t;
      }
      setTranscript((finalText + " " + interim).trim());
    };
    rec.onerror = (e) => {
      setListening(false);
      if (e.error === "not-allowed") setPermError("Microphone permission denied. Allow access in your browser settings.");
      else setPermError(`Mic error: ${e.error || "unknown"}`);
    };
    rec.onend = async () => {
      setListening(false);
      const text = (finalText || "").trim();
      if (text) {
        await handleAsk(text);
      }
    };
    recRef.current = rec;
    try {
      rec.start();
      setListening(true);
    } catch (e) {
      setPermError(e.message || "Failed to start microphone");
    }
  };

  const stopListening = () => {
    try { recRef.current && recRef.current.stop(); } catch {}
    setListening(false);
  };

  const handleAsk = async (text) => {
    setTranscript(text);
    setReply("");
    setThinking(true);
    try {
      const token = localStorage.getItem("thn_token");
      const { data } = await api.post("/chat", { session_id: sessionId, message: text });
      if (!sessionId && data.session_id) {
        setSessionId(data.session_id);
        localStorage.setItem(VOICE_SESSION, data.session_id);
      }
      setReply(data.reply);
      // TTS
      speakReply(data.reply);
    } catch (e) {
      setReply("Sorry — I couldn't process that. Please try again.");
    } finally {
      setThinking(false);
    }
  };

  const speakReply = async (text) => {
    // Strip URLs from the spoken portion for cleaner audio
    const spoken = text.replace(/https?:\/\/\S+/g, "the link on your screen");
    setSpeaking(true);
    try {
      const res = await fetch(`${API}/voice/tts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ text: spoken.slice(0, 3500), voice: "nova" }),
      });
      if (!res.ok) throw new Error("TTS failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => { setSpeaking(false); URL.revokeObjectURL(url); };
      audio.onerror = () => { setSpeaking(false); URL.revokeObjectURL(url); };
      await audio.play();
    } catch {
      setSpeaking(false);
    }
  };

  // Match URLs in the reply against known products → enriched cards
  const replyUrls = extractUrls(reply);
  const matchedProducts = products.filter(
    (p) => p.url && p.url !== "#" && replyUrls.some((u) => u.startsWith(p.url))
  );
  // also surface any unmatched URLs (e.g. demo videos) as generic link buttons
  const extraUrls = replyUrls.filter(
    (u) => !matchedProducts.some((p) => u.startsWith(p.url))
  );

  const node = (
    <>
      {!open && (
        <button
          data-testid="voice-launcher"
          onClick={() => setOpen(true)}
          className="fixed bottom-24 right-6 z-50 w-14 h-14 rounded-full bg-[#3B82F6] text-[#08060F] flex items-center justify-center shadow-2xl hover:scale-105 transition-transform"
          aria-label="Open voice agent"
          title="Talk to Zero"
        >
          <Mic size={22} />
        </button>
      )}

      {open && (
        <div
          data-testid="voice-panel"
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          style={{ background: "rgba(10, 15, 26, 0.78)", backdropFilter: "blur(8px)" }}
        >
          <div className="w-full max-w-2xl glass rounded-3xl overflow-hidden border border-white/10 shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#3B82F6] to-[#22D3EE] flex items-center justify-center text-[#08060F]">
                  <Sparkles size={18}/>
                </div>
                <div>
                  <div className="text-white font-semibold">Talk to Zero</div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#22D3EE]">Voice Agent</div>
                </div>
              </div>
              <button data-testid="voice-close" onClick={() => setOpen(false)} className="text-[#94A3B8] hover:text-white p-1"><X/></button>
            </div>

            <div className="p-8 grid md:grid-cols-2 gap-8 items-center">
              {/* Mic side */}
              <div className="flex flex-col items-center text-center">
                {!supported && (
                  <div className="text-sm text-[#3B82F6] bg-[#3B82F6]/10 border border-[#3B82F6]/30 rounded-lg p-3 mb-4">
                    Voice input isn't supported in this browser. Try Chrome, Edge, or Safari.
                  </div>
                )}
                {permError && (
                  <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4" data-testid="voice-perm-error">{permError}</div>
                )}
                <button
                  data-testid="voice-mic-btn"
                  onClick={listening ? stopListening : startListening}
                  disabled={!supported || thinking}
                  className={`relative w-32 h-32 rounded-full flex items-center justify-center transition-transform ${
                    listening
                      ? "bg-[#3B82F6] text-[#08060F] scale-105"
                      : thinking
                      ? "bg-white/10 text-white"
                      : "bg-[#22D3EE] text-[#08060F] hover:scale-105"
                  } disabled:opacity-50`}
                >
                  {listening && (
                    <span className="absolute inset-0 rounded-full animate-ping bg-[#3B82F6]/40"/>
                  )}
                  {thinking ? <Loader2 size={42} className="animate-spin"/> : listening ? <Mic size={42}/> : <MicOff size={42}/>}
                </button>
                <div className="mt-5 text-sm text-[#94A3B8]">
                  {listening ? "Listening… click to stop." : thinking ? "Thinking…" : speaking ? "Speaking…" : "Tap to talk"}
                </div>
                {speaking && (
                  <div className="mt-3 inline-flex items-center gap-2 text-xs text-[#22D3EE]">
                    <Volume2 size={14}/> Playing reply
                  </div>
                )}
              </div>

              {/* Transcript + reply side */}
              <div className="space-y-4 min-h-[260px]">
                <div>
                  <div className="eyebrow">You said</div>
                  <div className="mt-2 text-white min-h-[44px] text-sm leading-relaxed" data-testid="voice-transcript">
                    {transcript || <span className="text-[#64748B] italic">Try: "What products do you offer?"</span>}
                  </div>
                </div>
                <div>
                  <div className="eyebrow">Zero</div>
                  <div className="mt-2 text-white min-h-[60px] text-sm leading-relaxed whitespace-pre-wrap" data-testid="voice-reply">
                    {reply ? renderWithLinks(reply) : <span className="text-[#64748B] italic">Reply will appear here.</span>}
                  </div>
                </div>

                {(matchedProducts.length > 0 || extraUrls.length > 0) && (
                  <div className="grid sm:grid-cols-2 gap-3" data-testid="voice-link-cards">
                    {matchedProducts.map((p) => (
                      <a
                        key={p.id}
                        href={p.url}
                        target="_blank"
                        rel="noreferrer"
                        className="card p-3 flex gap-3 hover:border-[#22D3EE]/40"
                      >
                        <img src={p.image} alt={p.name} className="w-14 h-14 rounded-lg object-cover"/>
                        <div className="min-w-0">
                          <div className="text-white text-sm font-semibold truncate">{p.name}</div>
                          <div className="text-[10px] uppercase tracking-[0.15em] text-[#22D3EE]">{p.tag}</div>
                          <div className="text-xs text-[#94A3B8] mt-1 inline-flex items-center gap-1">
                            <ExternalLink size={10}/> Visit
                          </div>
                        </div>
                      </a>
                    ))}
                    {extraUrls.map((u) => (
                      <a
                        key={u}
                        href={u}
                        target="_blank"
                        rel="noreferrer"
                        className="card p-3 flex items-center gap-3 hover:border-[#3B82F6]/40"
                      >
                        <div className="w-10 h-10 rounded-lg bg-[#3B82F6]/10 text-[#3B82F6] flex items-center justify-center"><Play size={16}/></div>
                        <div className="min-w-0">
                          <div className="text-white text-sm font-semibold truncate">Open link</div>
                          <div className="text-xs text-[#94A3B8] truncate">{new URL(u).hostname}</div>
                        </div>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );

  if (typeof document === "undefined") return null;
  return createPortal(node, document.body);
}
